'use client';

import { useUserLocale } from '@/hooks/useUserLocale';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { 
  FaInstagram, 
  FaTelegram, 
  FaTiktok, 
  FaXTwitter, 
  FaFacebook,
  FaCcVisa, 
  FaCcMastercard, 
  FaApplePay,
  FaCcAmex,
  FaCcJcb
} from 'react-icons/fa6';
import { useSelector } from 'react-redux';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { locale, t } = useUserLocale();
  const pathname = usePathname();
  const isFlutterWebview = useSelector((state: any) => state.app.isFlutterWebview);
  const hideRoutes = ['/welcome/tryit', '/welcome/complete-account', '/onboard', '/learning-feed', '/contacts', 
                      '/languages', '/inbox', '/balance',
                      '/dashboard', '/vocab', /^\/story\/.*/, /^\/chat\/[a-zA-Z0-9]+$/, /\/practice\/[a-zA-Z0-9]+$/, 
                      /\/practice\/[a-zA-Z0-9]+\/history$/, /\/exam\/[a-zA-Z0-9]+\/.*/, /\/webview\/.*/]; // exclusion routes, some routes are patterns (e.g. practice/:id)

  let shouldHide = hideRoutes.some(route => {
    if (typeof route === 'string') {
      return pathname.includes(route);
    }

    return pathname.match(route);
  });

  if (isFlutterWebview) { shouldHide = true; }

  const socialLinks = useMemo(() => {
    return [
      {
        label: 'Instagram',
        url: 'https://www.instagram.com/chatlanga',
        icon: <FaInstagram className="w-5 h-5" />
      },
      {
        label: 'Telegram',
        url: 'https://t.me/chatlanga_app',
        icon: <FaTelegram className="w-5 h-5" />
      },
      {
        label: 'Tiktok',
        url: '',
        icon: <FaTiktok className="w-5 h-5" />
      },
      {
        label: 'X',
        url: 'https://x.com/chatlanga_app',
        icon: <FaXTwitter className="w-5 h-5" />
      },
      {
        label: 'Facebook',
        url: '',
        icon: <FaFacebook className="w-5 h-5" />
      },      
    ];
  }, []);

  const paymentTypes = useMemo(() => {
    return [
      {
        label: 'Visa',
        icon: <FaCcVisa className="w-6 h-6" />
      }, 
      {
        label: 'Apple Pay',
        icon: <FaApplePay className="w-6 h-6" />
      },
      {
        label: 'Mastercard',
        icon: <FaCcMastercard className="w-6 h-6" />
      },
      {
        label: 'Amex',
        icon: <FaCcAmex className="w-6 h-6" />
      },
      {
        label: 'JCB',
        icon: <FaCcJcb className="w-6 h-6" />
      },
    ];
  }, []);

  if (shouldHide) {
    return null;
  }

  return (
    <footer className="text-gray-400 py-8 pb-10 mt-auto">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col justify-between items-center">
            <div className="mb-4 text-center md:text-left">
              <p className="text-sm">© {currentYear} Chatlanga, Warehouse Robotics S.L.</p>
            </div>
            <div className="flex flex-col items-center text-center md:text-left">              
              <p className="text-sm mb-2">
                <span className='text-xs'>{t.footer.support}:</span>&nbsp;  
                <a href="mailto:support@wrobo.io" className="underline hover:text-blue-500 transition-colors">support@wrobo.io</a>
              </p>
              <div className='grid grid-cols-2 gap-2 mb-2'>
                <Link href='/page/content/terms_of_service' className='text-xs underline hover:text-secondary-500 transition-colors'>{t.footer.termsOfService}</Link>
                <Link href='/page/content/privacy_policy' className='text-xs underline hover:text-secondary-500 transition-colors'>{t.footer.privacyPolicy}</Link>
              </div>
              <div className="max-w-xs text-xs text-center">Warehouse Robotics S.L., Selva de Mar, 9, 08005 Barcelona, Spain</div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Social Links */}
            <div className="flex flex-col space-y-3 items-center md:items-start">
              {/* <h4 className="font-medium text-sm uppercase tracking-wider">Connect With Us</h4> */}
              <div className="flex space-x-4 items-center sm:h-6">
                {socialLinks.map((link, index) => (
                  <a 
                    key={index} 
                    href={link.url || '#'} 
                    aria-label={link.label}
                    className="text-gray-500 hover:text-blue-500 transition-colors duration-200"
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="flex flex-col space-y-3 items-center">
              {/* <h4 className="font-medium text-sm uppercase tracking-wider">Payment Methods</h4> */}
              <div className="flex space-x-3 md:h-6">
                {paymentTypes.map((payment, index) => (
                  <div key={index} className="text-gray-500">
                    {payment.icon}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
} 